import React, { useState, useEffect } from 'react';
import {
  leaveSlotService,
  LeaveSlotCreate,
  AffectedAppointment,
  SelectiveAppointmentHandling,
  AppointmentAction,
} from '../../../services/availability/leaveSlotService';
import './SelectiveAppointmentHandling.css';

interface SelectiveAppointmentHandlingProps {
  practiceId: number;
  leaveSlot: LeaveSlotCreate;
  conflictingAppointments: AffectedAppointment[];
  onClose: () => void;
  onSuccess: () => void;
}

type AppointmentActionState = 'cancel' | 'reschedule' | 'keep';

const SelectiveAppointmentHandlingView: React.FC<SelectiveAppointmentHandlingProps> = ({
  practiceId,
  leaveSlot,
  conflictingAppointments,
  onClose,
  onSuccess,
}) => {
  const [appointmentActions, setAppointmentActions] = useState<Record<number, AppointmentActionState>>({});
  const [rescheduleDates, setRescheduleDates] = useState<Record<number, string>>({});
  const [rescheduleTimes, setRescheduleTimes] = useState<Record<number, string>>({});
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize all appointments to "keep" by default
    const initialActions: Record<number, AppointmentActionState> = {};
    const initialDates: Record<number, string> = {};
    const initialTimes: Record<number, string> = {};

    conflictingAppointments.forEach((apt) => {
      initialActions[apt.id] = 'keep';
      const aptDate = new Date(apt.appointment_date);
      initialDates[apt.id] = formatDateString(aptDate);
      initialTimes[apt.id] = formatTimeString(aptDate);
    });

    setAppointmentActions(initialActions);
    setRescheduleDates(initialDates);
    setRescheduleTimes(initialTimes);
  }, [conflictingAppointments]);

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeString = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatAppointmentDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isFormValid = (): boolean => {
    for (const appointment of conflictingAppointments) {
      const action = appointmentActions[appointment.id];
      if (action === 'reschedule') {
        const date = rescheduleDates[appointment.id];
        const time = rescheduleTimes[appointment.id];
        if (!date || !time) {
          return false;
        }
        const rescheduleDateTime = new Date(`${date}T${time}`);
        if (rescheduleDateTime < new Date()) {
          return false; // Can't reschedule to past
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields for rescheduled appointments.');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const appointmentActionsList: AppointmentAction[] = conflictingAppointments.map((appointment) => {
        const action = appointmentActions[appointment.id];
        switch (action) {
          case 'cancel':
            return {
              appointment_id: appointment.id,
              action: 'cancel',
            };
          case 'reschedule':
            const date = rescheduleDates[appointment.id];
            const time = rescheduleTimes[appointment.id];
            const rescheduleDateTime = new Date(`${date}T${time}`);
            return {
              appointment_id: appointment.id,
              action: 'reschedule',
              new_appointment_date: rescheduleDateTime.toISOString(),
            };
          case 'keep':
          default:
            return {
              appointment_id: appointment.id,
              action: 'keep',
            };
        }
      });

      const handling: SelectiveAppointmentHandling = {
        appointment_actions: appointmentActionsList,
      };

      await leaveSlotService.createLeaveWithSelectiveHandling(practiceId, leaveSlot, handling);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create leave with handling');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="selective-handling-view">
      <div className="selective-handling-header">
        <h2>Handle Conflicts</h2>
        <button type="button" className="selective-handling-close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="selective-handling-body">
        <div className="selective-handling-info">
          You have {conflictingAppointments.length} conflicting appointment(s). Choose how to handle
          each one.
        </div>

        {error && <div className="selective-handling-error">{error}</div>}

        <div className="selective-handling-appointments">
          {conflictingAppointments.map((appointment) => (
            <div key={appointment.id} className="appointment-action-card">
              <div className="appointment-action-header">
                <div className="appointment-patient-name">
                  {appointment.patient_name || `Patient #${appointment.user_id || appointment.id}`}
                </div>
                <div className="appointment-date">
                  {formatAppointmentDate(appointment.appointment_date)}
                </div>
              </div>

              <div className="appointment-action-selector">
                <label>
                  <input
                    type="radio"
                    name={`action-${appointment.id}`}
                    value="keep"
                    checked={appointmentActions[appointment.id] === 'keep'}
                    onChange={() => {
                      setAppointmentActions((prev) => ({ ...prev, [appointment.id]: 'keep' }));
                    }}
                  />
                  <span>Keep</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name={`action-${appointment.id}`}
                    value="cancel"
                    checked={appointmentActions[appointment.id] === 'cancel'}
                    onChange={() => {
                      setAppointmentActions((prev) => ({ ...prev, [appointment.id]: 'cancel' }));
                    }}
                  />
                  <span>Cancel</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name={`action-${appointment.id}`}
                    value="reschedule"
                    checked={appointmentActions[appointment.id] === 'reschedule'}
                    onChange={() => {
                      setAppointmentActions((prev) => ({ ...prev, [appointment.id]: 'reschedule' }));
                    }}
                  />
                  <span>Reschedule</span>
                </label>
              </div>

              {appointmentActions[appointment.id] === 'reschedule' && (
                <div className="appointment-reschedule-fields">
                  <input
                    type="date"
                    value={rescheduleDates[appointment.id] || ''}
                    min={formatDateString(new Date())}
                    onChange={(e) => {
                      setRescheduleDates((prev) => ({ ...prev, [appointment.id]: e.target.value }));
                    }}
                    className="selective-handling-input"
                  />
                  <input
                    type="time"
                    value={rescheduleTimes[appointment.id] || ''}
                    onChange={(e) => {
                      setRescheduleTimes((prev) => ({ ...prev, [appointment.id]: e.target.value }));
                    }}
                    className="selective-handling-input"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="selective-handling-footer">
        <button type="button" className="selective-handling-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="selective-handling-save"
          onClick={handleSubmit}
          disabled={isCreating || !isFormValid()}
        >
          {isCreating ? 'Creating...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default SelectiveAppointmentHandlingView;

